/**
 * Admin "View as" (SA-906): the switch between identities.
 *
 * `startViewAs` mints the token and swaps the store. `settleViewAsEnd` is what
 * the banner calls once the store records an end: Stop, the countdown, or a
 * 401 in apiClient. The store is the one event bus.
 *
 * Neither function clears anything. What the previous identity left behind is
 * cleared at the swap itself by the identity-change subscription
 * (services/identityChange.ts, SA-922); these two only reload permissions for
 * the new identity. `useAppInit` re-runs for the new identity on its own,
 * keyed on `user.id`.
 */
import { apiClient } from '@/services/apiClient'
import { useAuthStore, type AuthUser, type ImpersonationEnded } from '@/stores/authStore'
import { usePermissionStore } from '@/stores/permissionStore'

export interface ImpersonationResponse {
  access_token: string
  expires_at: string
  user: AuthUser
  must_change_password: boolean
  must_accept_disclaimer: boolean
}

/** Throws `ApiError` on a refusal; the store is untouched then. */
export async function startViewAs(userId: string): Promise<void> {
  const res = await apiClient.post<ImpersonationResponse>('/api/admin/impersonation', { user_id: userId })
  useAuthStore.getState().startImpersonation(
    { ...res.user, roles: res.user.roles ?? [] },
    res.access_token,
    res.expires_at,
    { mustChangePassword: !!res.must_change_password, mustAcceptDisclaimer: !!res.must_accept_disclaimer },
  )
  await usePermissionStore.getState().loadPermissions()
}

/** Settle an end the store recorded: permissions for the admin, tell the
 * server (best effort — the token is already dropped), clear the marker. */
export async function settleViewAsEnd(): Promise<ImpersonationEnded | null> {
  const ended = useAuthStore.getState().impersonationEnded
  if (!ended) return null
  await usePermissionStore.getState().loadPermissions()
  try {
    await apiClient.fetch(`/api/admin/impersonation/${encodeURIComponent(ended.targetUser.id)}`, { method: 'DELETE' })
  } catch {
    // Best effort by design (spec §3.3): nothing was stored, nothing to revoke.
  }
  useAuthStore.getState().clearImpersonationEnded()
  return ended
}
